import React, { useCallback, useEffect } from "react";
import Filter from "bad-words";
import useCustomBadWordsFilter from 'hooks/useCustomBadWordsFilter'; // Assicurati di specificare il percorso corretto

import useShowLayout from "hooks/useShowLayout";
import Keyboard from "components/keyboard";
import useIsMobile from "hooks/useIsMobile";
import StyledButton from "components/styled-button";
import useIsTablet from "hooks/useIsTablet";
import NextStepArrow from "assets/icons/next-step-arrow";
import ErrorAlert from "components/error-alert";
import Scribe from "components/scribe";
import useIsPc from "hooks/useIsPc";
import { allowedCharacters } from "configs/generated-page-configs";
import "components/keyboard/keyboard.css";
import "components/error-alert/error-alert.css";
import "./form.css";
import useBoolean from "hooks/useBoolean";
import useNavigation from "hooks/useNavigation";
import { useLayoutContext } from "../../layout/layout-provider";
import { useFormContext } from "contexts/form/form-provider";
import { useScribeContext } from "contexts/scribe/scribe-provider";

import app from "../../App";

const IT_PLACEHOLDER = "Scrivi il tuo nome";
const EN_PLACEHOLDER = "Write your name";

const IT_BAD_WORD_ERROR = "…Scrivi con saggezza e prova ancora! Grazie!";
const EN_BAD_WORD_ERROR = "…Write with wisdom and try again! Thanks!";

const IT_CHAR_NOT_ALLOWED_ERROR = "…Mi dispiace: Non conosco questa lettera.";
const EN_CHAR_NOT_ALLOWED_ERROR = "…I'm sorry, I don't know this letter.";

const IT_ONLY_2_WORDS_ERROR = "…Non più di 2 nomi!";
const EN_ONLY_2_WORDS_ERROR = "…No more than 2 names!";

const IT_NAME_TOO_LONG_ERROR = "…Il nome è troppo lungo! (max 14 lettere)";
const EN_NAME_TOO_LONG_ERROR = "…The name is too long! (max 14 letters)";

const IT_NAME_TOO_SHORT_ERROR = "…Il nome è troppo corto! (min 3 caratteri)";
const EN_NAME_TOO_SHORT_ERROR = "…The name is too short! (min 3 characters)";

const badWordsFilter = new Filter();



type ErrorMessage = {
  ita: string;
  eng: string;
  enabled: boolean;
};

const Form: React.FC = () => {

  const customFilter = useCustomBadWordsFilter();
  //const extendedFilter = new Filter();
  //customFilter.list.forEach(parola =>   extendedFilter.addWords(parola));


  const { setFirstLayerHidden, setSecondLayerHidden, setScribeHidden , setCardReady } =
    useLayoutContext();
  const [disableInput, setDisableInput] = React.useState<boolean>(true);
  const { inputValue, setInputValue } = useFormContext();
  const { setShowWritingAnimation, setInitialized, setShowQrCode, setSwitchToQrCodeScribe } = useScribeContext();
  const [errorMessage, setErrorMessage] = React.useState<ErrorMessage>({
    ita: "",
    eng: "",
    enabled: false,
  });
  const isPc = useIsPc();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const { navigateToResult } = useNavigation();

  const [appeared, setAppeared] = React.useState(false);

  useShowLayout();

  //inizializza lo scriba
  useEffect(() => {

    //TODO: gestire anche caso in cui lo scriba non e inizializzato, cosi in ogni caso si inizializza
    setInitialized(true);
  }, [setInitialized]);

  useEffect(() => {
    setFirstLayerHidden(true); //hidden
    setSecondLayerHidden(true); //hidden
    setScribeHidden(false); //hidden
    setSwitchToQrCodeScribe(false); 
    setShowQrCode(false);
  }, [setFirstLayerHidden, setSecondLayerHidden, setScribeHidden, setSwitchToQrCodeScribe, setShowQrCode]);

  useEffect(() => {

    if(isMobile)
    {
    }
    setCardReady(false); //nel form e false


  }, [isMobile, setCardReady]);



  const showErrorMessage = useCallback((ita: string, eng: string) => {
    setErrorMessage({
      ita,
      eng,
      enabled: true,
    });

    setTimeout(() => {
      setErrorMessage({
        ita: "",
        eng: "",
        enabled: false,
      });
    }, 3000);
  }, []);

  const clearInput = useCallback(() => {
    setInputValue("");
  }, [setInputValue]);

  const goToResultPage = useCallback(() => {
    clearInput();
    setTimeout(() => {
      navigateToResult(inputValue);
    }, 2000);

  }, [clearInput, inputValue, navigateToResult]);

  const onInputChange = useCallback(
    (char: string) => {
      if (char === " ") {
        if (inputValue.trim().split(" ").length >= 2) {
          showErrorMessage(IT_ONLY_2_WORDS_ERROR, EN_ONLY_2_WORDS_ERROR);
          return;
        }
        setInputValue((prev) => prev.trim() + " ");
        return;
      }

      const latestWord = inputValue.split(" ").pop() || "";

      if (latestWord.length >= 14) {
        showErrorMessage(IT_NAME_TOO_LONG_ERROR, EN_NAME_TOO_LONG_ERROR);
        return;
      }

      if (!allowedCharacters.includes(char.toLowerCase())) {
        showErrorMessage(IT_CHAR_NOT_ALLOWED_ERROR, EN_CHAR_NOT_ALLOWED_ERROR);
        return;
      }

      setInputValue((prev) => prev + char);
    },
    [inputValue, setInputValue, showErrorMessage],
  );

  const onClickChar = useCallback(
    (char: string) => {
      if (disableInput) return;

      if (char === "←") {
        setInputValue((prev) => prev.slice(0, -1));
        return;
      }

      onInputChange(char);
    },
    [disableInput, onInputChange, setInputValue],
  );

  const handleInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      if (disableInput) return;

      let value = (evt.target.value ?? "").toUpperCase();

      if (value.length < inputValue.length) {
        setInputValue(value);
        return;
      }

      onInputChange(value[value.length - 1]);
    },
    [disableInput, inputValue.length, onInputChange, setInputValue],
  );



  const handleSubmission = useCallback(() => {
    const name = inputValue.trim();

    if (name.length < 3) {
      showErrorMessage(IT_NAME_TOO_SHORT_ERROR, EN_NAME_TOO_SHORT_ERROR);
      return;
    }

    /*
    // Verifica se una parola vietata è presente senza filtrare completamente
    const isProfane = customFilter.filter.isProfane(name);

    if (isProfane) {
      showErrorMessage(IT_BAD_WORD_ERROR, EN_BAD_WORD_ERROR);
      return;
    }*/

    //const filteredInput = badWordsFilter.clean(inputValue);
    const filteredInput = customFilter.filterBadwords(inputValue);

    if (filteredInput.includes("*")) {
      showErrorMessage(IT_BAD_WORD_ERROR, EN_BAD_WORD_ERROR);
      return;
    }

    setDisableInput(true);


      if (isMobile) {
        goToResultPage();
        return;
      } else {
        setShowWritingAnimation(true);
        goToResultPage();
      }


  }, [customFilter.filterBadwords, goToResultPage, inputValue, isMobile, setShowWritingAnimation, showErrorMessage]);

  useEffect(() => {
    if (!errorMessage.enabled) return;

    const input = document.querySelector("#input-name input") as HTMLElement;
    const errorAlert = document.querySelector(
      "#error-alert-container",
    ) as HTMLElement;

    const inputBoundingRect = input.getBoundingClientRect();

    errorAlert.style.top = `${inputBoundingRect.height}px`;
  }, [errorMessage, isTablet]);

  useEffect(() => {
    setDisableInput(false);
  }, []);

  const handleKeyPress = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          handleSubmission();
        }
      },
      [handleSubmission]
  );

  return (
    <>
      <div
        id="input-and-keyboard-container"
        className="flex direction-column align-center gap-50 pc-gap-80 z-index-1 mobile-width-100 tablet-width-100 mobile-padding-x-50 tablet-padding-x-50"
      >
        <div
          id="input-name"
          className="flex direction-column pc-direction-row width-100 pc-gap-10 gap-50 tablet-gap-80"
        >
          <div
            className={`relative flex justify-center align-center width-100 ${
              disableInput ? "fade-out" : "fade-in"
            }`}
            onTransitionEnd={() => {
             setAppeared(true)
            }}
          >
            <input
              className={` ${disableInput ? "fade-out" : "fade-in"}`}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyUp={handleKeyPress}
            />
            {inputValue && (
              <button
                onClick={clearInput}
                className={`clear-button ${
                  disableInput ? "fade-out" : "fade-in"
                }`}
                disabled={disableInput}
              ></button>
            )}
            {inputValue === "" && appeared && (
              <>
                <p className="absolute uppercase ">{IT_PLACEHOLDER}</p>
                <p className="absolute uppercase ">{EN_PLACEHOLDER}</p>
              </>
            )}
            {errorMessage.enabled && (
              <div
                id="error-alert-container"
                className="flex align-center justify-center absolute width-100"
              >
                <ErrorAlert ita={errorMessage.ita} eng={errorMessage.eng} />
              </div>
            )}
          </div>
          <StyledButton
            onClick={handleSubmission}
            id="next-step-button"
            className={` ${disableInput ? "fade-out" : "fade-in"}`}
          >
            <NextStepArrow />
          </StyledButton>
        </div>
        {isPc && (
          <Keyboard
            className={`z-index-1 ${
              disableInput ? "fade-out delay-1" : "fade-in delay-1"
            }`}
            onCharClick={onClickChar}
          />
        )}
      </div>
    </>
  );
};

export default Form;
